export const PREQUAL_COPY = {
  FORM_SUBTITLE:
    "Check your eligibility for a monthly subscription plan in less than 1 minute.",
  CTA: "Check Eligibility",
  PROCESSING_TITLE: "Checking Your Options",
  PROCESSING_SUBTITLE: "Hang tight - this only takes a few seconds.",
  ERROR_TITLE: "We couldn't complete your eligibility check",
  ERROR_SUBTITLE: "No credit impact — you can try again.",
} as const;

export const PROCESSING_STEPS = [
  "Verifying your identity",
  "Confirming your eligibility",
  "Matching you to billing options",
] as const;

/** Checklist timers while the prequalify mutation is in flight. */
export const PROCESSING_STEP_TIMERS_MS = [1500, 3500] as const;
/** Delay between check-offs once the mutation has resolved. */
export const PROCESSING_FAST_FORWARD_MS = 300;
/** Hold on the completed checklist before moving to the result. */
export const PROCESSING_COMPLETE_HOLD_MS = 800;

export type ScratchDisclosureKey =
  | "SMS_CONSENT"
  | "ELECTRONIC_COMMUNICATIONS"
  | "PRIVACY_POLICY"
  | "CREDIT_PULL_AUTHORIZATION";

export interface IScratchDisclosure {
  key: ScratchDisclosureKey;
  label: string;
  /** Scratch-hosted legal document, opened directly in a new tab. */
  url: string;
}

// Canonical URLs from the Figma design comments (Pre-Qualification Flow,
// threads #966–#969); shared with the tablet app's scratch-legal-documents.
export const SCRATCH_DISCLOSURES: IScratchDisclosure[] = [
  {
    key: "SMS_CONSENT",
    label: "Subscription Program SMS/Text Message Policy",
    url: "https://scratchpay.com/legal/subscription-sms",
  },
  {
    key: "ELECTRONIC_COMMUNICATIONS",
    label: "Subscription Program Electronic Signatures, Records, Disclosures & Communications Consent Agreement",
    url: "https://scratchpay.com/legal/eca",
  },
  {
    key: "PRIVACY_POLICY",
    label: "Subscription Program Privacy Policy",
    url: "https://scratchpay.com/legal/subscription-privacy",
  },
  {
    key: "CREDIT_PULL_AUTHORIZATION",
    label: "Subscription Program Credit Pull Consent & Authorization",
    url: "https://scratchpay.com/legal/subscription-credit-pull-authorization",
  },
];

export const PREQUALIFICATION_ERROR_CODES = {
  INVALID_PHONE: "PREQUALIFICATION_INVALID_PHONE",
} as const;

/**
 * Result-screen palette (Figma 35828-52079 / 35825-51251), shared by the
 * result/gate components. Kept as inline-style values rather than Tailwind
 * arbitrary classes so a design tweak lands in one place.
 */
export const PREQUAL_COLORS = {
  /** Turquoise/200 — check circle fill, "Available to you" line. */
  teal: "#257B7A",
  /** Brand gold — status-pill border, sparkle dots. */
  gold: "#E5AF0A",
  /** Pale gold — outer ring of the check circle. */
  goldRing: "#F3D683",
  /** Cream — pill background. */
  cream: "#FDF8EC",
  /** Gold/15 — billing-card icon chip background. */
  iconChip: "#F9EED5",
  /** Dark gold — billing-card icon. */
  iconGold: "#B98A0B",
  /** Taupe/50 — billing-card border. */
  cardBorder: "#E8DFD7",
  /** Violet/100 — highlighted billing-card border (annual-only state). */
  cardBorderHighlight: "#7588DF",
  /** Violet/200 — "MOST POPULAR" badge. */
  violet: "#637AE2",
  /** Black/100 — display headings. */
  textDark: "#262626",
  /** Black/50 — card descriptions, "Powered by" lockup text. */
  textMuted: "#908E8D",
} as const;
